/**
 * MorphReveal — the loader's placeholder card turns into the real card in place.
 *
 * On mount during the reveal window, a skeleton identical to the loader's sits
 * over the card. A soft band of light then wipes top→bottom across it: the
 * skeleton is clipped away behind the edge (counter-translated, so it stays put
 * while its mask moves) and the real card shows through. Staggered by index.
 *
 * Transform-only (no opacity) because the web shell forces low-opacity elements
 * to opacity 1. Cards mounted later (pagination, filters) render untouched, and
 * the wrapper is always present so a card never remounts when the window ends.
 */
import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated, Easing, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { SkeletonBones, CARD_FILL, SGP_CARD_FILL, CARD_GAP, OPAQUE_BASE, useReducedMotion } from './ResultSkeletonCard';
import { shouldMorph, revealDelayMs, REVEAL_DURATION_MS } from '../utils/loadingPhase';

const EDGE_HEIGHT = 72;

export default function MorphReveal({ index, active, sgp = false, compact = false, children }) {
  const reducedMotion = useReducedMotion();
  // Same breakpoint as the real card's mobile layout (useUnifiedBetCard: window < 768).
  const { width: windowWidth } = useWindowDimensions();
  const compactLayout = compact || windowWidth < 768;
  // Decided once, at mount: only the first screenful of a fresh result set morphs.
  const [covered, setCovered] = useState(() => shouldMorph(index, active));
  // The wipe is this card's entrance, so its own slide-in is skipped (read
  // once at the card's mount; it never replays).
  const startedCovered = useRef(covered).current;
  const [size, setSize] = useState({ width: 0, height: 0 });
  const measured = size.height > 0;
  const wipe = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!covered) return undefined;
    if (reducedMotion) { setCovered(false); return undefined; }
    // Starts once the card has a size; later height changes (content loading in)
    // only resize the overlay — they must not restart the wipe.
    if (!measured) return undefined;
    const anim = Animated.timing(wipe, {
      toValue: 1,
      duration: REVEAL_DURATION_MS,
      delay: revealDelayMs(index),
      easing: Easing.bezier(0.65, 0, 0.35, 1),
      useNativeDriver: false,
    });
    anim.start(({ finished }) => { if (finished) setCovered(false); });
    return () => anim.stop();
  }, [covered, measured, reducedMotion, index, wipe]);

  const travel = size.height + EDGE_HEIGHT;
  const maskY = wipe.interpolate({ inputRange: [0, 1], outputRange: [0, travel] });
  const counterY = wipe.interpolate({ inputRange: [0, 1], outputRange: [0, -travel] });

  return (
    <View
      style={s.wrap}
      onLayout={covered ? (e) => setSize({ width: e.nativeEvent.layout.width, height: e.nativeEvent.layout.height - CARD_GAP }) : undefined}
    >
      {React.isValidElement(children)
        ? React.cloneElement(children, startedCovered ? { animateMount: false } : { mountDelay: Math.min(Math.max(0, index) * 70, 420) })
        : children}
      {covered && (
        <View pointerEvents="none" style={[s.frame, { bottom: CARD_GAP }]}>
          <Animated.View style={[s.mask, { transform: [{ translateY: maskY }] }]}>
            <Animated.View style={[s.panel, { height: size.height || '100%', transform: [{ translateY: counterY }] }]}>
              <LinearGradient colors={sgp ? SGP_CARD_FILL : CARD_FILL} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={StyleSheet.absoluteFill} />
              <SkeletonBones seed={index} compact={compactLayout} />
            </Animated.View>
            {/* Band of light riding the wipe boundary */}
            <LinearGradient
              colors={['rgba(196,181,253,0.55)', 'rgba(139,92,246,0.22)', 'rgba(139,92,246,0)']}
              start={{ x: 0.5, y: 0 }}
              end={{ x: 0.5, y: 1 }}
              style={s.edge}
            />
          </Animated.View>
        </View>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  wrap: { width: '100%', alignSelf: 'stretch', position: 'relative' },
  frame: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    borderRadius: 16,
    overflow: 'hidden',
  },
  mask: { position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, overflow: 'hidden' },
  panel: {
    position: 'absolute', top: 0, left: 0, right: 0,
    backgroundColor: OPAQUE_BASE,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)', borderTopColor: 'rgba(255,255,255,0.22)',
    borderRadius: 16, overflow: 'hidden',
  },
  edge: { position: 'absolute', top: 0, left: 0, right: 0, height: EDGE_HEIGHT },
});
